import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';

function Video({
  src,
  poster,
  className = "",
  controls = true,
  autoplay = false,
  muted = false,
  loop = false,
  preload = "metadata",
  loading = "lazy",
  ...props
}) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const videoRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!videoRef?.current || loading === 'eager') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    observer?.observe(videoRef?.current);

    return () => {
      if (observer && videoRef?.current) {
        observer?.disconnect();
      }
    };
  }, [loading]);

  // Handle video load
  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  // Handle play/pause
  const handlePlay = () => {
    setIsPlaying(true);
    setShowPlayButton(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
    setShowPlayButton(true);
  };

  const togglePlay = () => {
    if (videoRef?.current) {
      if (isPlaying) {
        videoRef?.current?.pause();
      } else {
        videoRef?.current?.play();
      }
    }
  };

  return (
    <div ref={videoRef} className={`relative overflow-hidden bg-black ${className}`}>
      {/* Loading placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
          <div className="flex flex-col items-center gap-2">
            <Icon name="Video" size={48} className="text-muted-foreground animate-pulse" />
            <p className="text-sm text-muted-foreground">جاري تحميل الفيديو...</p>
          </div>
        </div>
      )}

      {/* Custom play button overlay */}
      {showPlayButton && isLoaded && !autoplay && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-all duration-300 z-10 group"
          aria-label="تشغيل الفيديو"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 hover:bg-primary flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
            <Icon name="Play" size={32} className="text-white ml-1" />
          </div>
        </button>
      )}

      {/* Video element */}
      {isInView && (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          controls={controls}
          autoPlay={autoplay}
          muted={muted}
          loop={loop}
          preload={preload}
          playsInline
          onLoadedData={handleLoadedData}
          onPlay={handlePlay}
          onPause={handlePause}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          {...props}
        >
          <track kind="captions" />
          متصفحك لا يدعم تشغيل الفيديو.
        </video>
      )}
    </div>
  );
}

export default Video;